import { useState, useEffect, useContext, useMemo } from "react";
import type { ElementType, ReactNode } from "react";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { Typography } from "@mui/material";
import { LoaderContext, GlobalContext } from "@/helpers/contexts/contexts";
import { reportService } from "@/shared/services/reportService";
import NoDataFound from "@/components/NoDataFound";
import { renderTemplate } from "@/shared/helpers/reportHelper";
import MultiBrandTotalSpendChartBase from "@/components/charts/MultiBrandTotalSpendChart";
import {
  getTotalSpendSum,
  getMultipleEmbedWidgetParams as getMultipleEmbedWidgetParamsBase,
} from "@/helpers/utils";
import PageLayout from "@/components/layouts/page/PageLayout";
import PageHeaderBase from "@/components/layouts/pageHeaders/PageHeader";
import { MUICustomAutocompleteSelect } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import useFetcher from "@/helpers/hooks/useFetcher";
import { CUSTOM_WIDGET_PARAM, USER_ROLE } from "@/config/constants";

type DashboardRecord = Record<string, any>;
type DataFilters = Record<
  string,
  {
    filter?: string;
    from?: string | Date;
    to?: string | Date;
  }
>;
type GlobalContextValue = {
  loggedInUser?: DashboardRecord;
  loggedInUserRole?: string;
  selectedBrand?: DashboardRecord;
  userBrands?: DashboardRecord[];
};
type Loader = (_show: boolean) => void;

const MultiBrandTotalSpendChart = MultiBrandTotalSpendChartBase as ElementType;
const PageHeader = PageHeaderBase as ElementType;
const getMultipleEmbedWidgetParams =
  getMultipleEmbedWidgetParamsBase as unknown as (
    _brands?: DashboardRecord[],
    _params?: typeof CUSTOM_WIDGET_PARAM,
    _selectedBrand?: DashboardRecord
  ) => DashboardRecord;

const ParentCompanyDashboardPage = () => {
  const loader = useContext(LoaderContext) as Loader;
  const location = useLocation();
  const { selectedBrand, loggedInUser, userBrands, loggedInUserRole } =
    useContext(GlobalContext) as GlobalContextValue;
  const [dataFilters, setDataFilters] = useState<DataFilters>({});

  const reqData = useMemo(() => {
    const selectedCompanyId =
      loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME
        ? loggedInUser?.companyData?.id
        : (loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) &&
            loggedInUser?.companyData?.company
          ? loggedInUser?.companyData?.company?.id
          : null;

    const reqData: DashboardRecord = {
      userId: loggedInUser?.id,
      dataType: "brand",
      id: selectedCompanyId,
      fk_brand_id:
        userBrands && userBrands.length > 0
          ? userBrands
              .map((brand: DashboardRecord) => brand?.id)
              .filter(Boolean)
              .join(",")
          : null,
    };

    if (dataFilters && !isEmpty(dataFilters)) {
      const filter_widget_id: string[] = [];
      Object.keys(dataFilters).forEach((key) => {
        if (dataFilters[key].from && dataFilters[key].to) {
          filter_widget_id.push(key);
          reqData[`fromdate_${key}`] = moment(dataFilters[key].from).format(
            "MM-DD-YYYY"
          );
          reqData[`todate_${key}`] = moment(dataFilters[key].to).format(
            "MM-DD-YYYY"
          );
        } else if (dataFilters[key].filter) {
          filter_widget_id.push(key);
          reqData[key] = dataFilters[key].filter
            .toLowerCase()
            .replace(/\s/g, "_");
        }
      });
      reqData["filter_widget_id"] = filter_widget_id;
    }

    return reqData;
    // Preserve the existing dashboard request refresh dependencies during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loggedInUser?.id,
    loggedInUser?.companyData?.id,
    userBrands,
    dataFilters,
  ]);

  const { data: report, isLoading: loading } = useFetcher(
    loggedInUser?.id && reqData,
    reportService.getOverviewDashboardReport,
    { revalidateOnFocus: false }
  );

  const handleOnChangeDataFilter = (id: string | number, filter: string) => {
    setDataFilters({ ...dataFilters, [id]: { filter } });
  };

  useEffect(() => {
    loader(loading);
  }, [loading, loader]);

  useEffect(() => {
    return () => loader(false);
    // Preserve the existing unmount cleanup dependency behavior during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <PageLayout>
        <NoDataFound message="Loading..." />
      </PageLayout>
    );
  }

  if (!report || !report.template.rows || report.template.rows.length === 0) {
    const NoDataFoundMessage =
      location.pathname === "/overview-dashboard"
        ? "There is no template defined for this parent company"
        : "There is no template defined for this brand";
    return (
      <PageLayout>
        <NoDataFound message={NoDataFoundMessage} />
      </PageLayout>
    );
  }

  const renderChartWidget = (
    widget: DashboardRecord,
    template?: DashboardRecord
  ): ReactNode => {
    let filterToRender: ReactNode = null;
    let chartToRender: ReactNode = null;

    const filters = template?.filters ? template.filters?.split(",") : [];
    if (template?.apply_filter && filters && filters.length) {
      const formattedFilters = filters.map((f: string) => {
        let value = f;
        return { label: f, value };
      });

      if (widget.widget_for === "parent-company" && widget.id === 275) {
        formattedFilters.unshift({
          label: "Last 3 Months",
          value: "Last 3 Months",
        });
      }

      filterToRender = (
        <div className="row text16P_Bold548E8D justify-content-end align-items-center">
          {widget.chart_type === "total_spend" && widget?.data?.length ? (
            <div className="col mb-1">
              <Typography color="text.primary" variant="body2" className="fw-b">
                Total: {getTotalSpendSum(widget)}
              </Typography>
            </div>
          ) : null}
          <div className="col mb-1">
            <div className="form-group d-flex align-items-center mb-0 justify-content-end">
              <MUICustomAutocompleteSelect
                options={formattedFilters.map((f) => ({
                  label: f.label,
                  value: f.value,
                }))}
                selectProps={{
                  onChange: (v) => {
                    handleOnChangeDataFilter(widget.id, v);
                  },
                  value: dataFilters[widget.id]
                    ? dataFilters[widget.id].filter
                    : "Last 3 Months",
                }}
                wrapperProps={{
                  style: { width: 150 },
                }}
                name="total_spend_filter"
                size="small"
                hideLabel
                className="mt-0"
              />
            </div>
          </div>
        </div>
      );

      if (widget.id === 8) {
        filterToRender = null;
      }
    }

    const isAllBrandDataEmpty =
      widget &&
      widget?.data?.every(
        (item: DashboardRecord) =>
          Array.isArray(item.data) && item.data.length === 0
      );
    if (!widget.data || widget.data.length === 0 || isAllBrandDataEmpty) {
      return (
        <>
          {filterToRender}
          <NoDataFound message="There is no record" height="100%" />
        </>
      );
    }

    if (!Array.isArray(widget.data)) {
      return (
        <>
          {filterToRender}
          <NoDataFound message="There is not in Array format" height="100%" />
        </>
      );
    }

    const labels = Object.keys(widget.data[0]);

    if (!labels || labels.length === 0) {
      return (
        <>
          {filterToRender}
          <NoDataFound message="There is no record" height="100%" />
        </>
      );
    }

    if (widget.chart_type === "total_spend") {
      if (
        Array.isArray(widget.data) &&
        widget?.data?.length > 0 &&
        widget?.data[0].system_generated_brand_id
      ) {
        chartToRender = (
          <MultiBrandTotalSpendChart
            data={widget.data}
            title={widget.alt_name || widget.name}
            formats={widget?.format}
            showTotal={true}
          />
        );
      }
    }

    return (
      <>
        <div>{filterToRender}</div>
        {chartToRender}
      </>
    );
  };

  const getBrandCount = () => {
    if (report?.totalBrand !== undefined) {
      return report.totalBrand;
    }
    return userBrands && userBrands.length > 0 ? userBrands.length : 0;
  };

  return (
    <PageLayout className="admin-dashboard-container mx-0 mx-sm-2">
      <PageHeader showName showLastUpdate showButton />
      <div className="pb-5 dashboard admin report">
        {renderTemplate({
          report: report,
          renderChartWidget,
          embedWidgetParams: getMultipleEmbedWidgetParams(
            userBrands,
            CUSTOM_WIDGET_PARAM,
            selectedBrand
          ),
          brandCount: getBrandCount(),
        })}
      </div>
    </PageLayout>
  );
};

export default ParentCompanyDashboardPage;
